import React from "react";
import {
  Container,
  Grid,
  Skeleton,
  Box,
  Paper,
} from "@mui/material";
import { HeadingGradient } from "../../Gradient";

/**
 * Shimmer loading component for InkMastersPage
 * Matches the structure of InkMastersPage with quest cards in sections
 */
const InkMastersPageShimmer = () => {
  // Quest Card Shimmer - matches the quest card structure
  const QuestCardShimmer = () => (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        borderRadius: 3,
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "transparent",
        backdropFilter: "blur(12px)",
        border: (theme) =>
          `1px solid ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)"
          }`,
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 2px 8px rgba(0,0,0,0.1)"
            : "0 2px 8px rgba(0,0,0,0.05)",
      }}
    >
      {/* Content Section Shimmer */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          p: { xs: 3, md: 3.5 },
          position: "relative",
        }}
      >
        {/* Status Badge Shimmer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Skeleton
            variant="rounded"
            width={70}
            height={24}
            sx={{ borderRadius: 1.5 }}
          />
          <Skeleton
            variant="rounded"
            width={50}
            height={24}
            sx={{ borderRadius: 1.5 }}
          />
        </Box>

        {/* Title Shimmer */}
        <Skeleton
          variant="text"
          width="90%"
          height={28}
          sx={{ mb: 0.5 }}
        />
        <Skeleton
          variant="text"
          width="70%"
          height={28}
          sx={{ mb: 1.5 }}
        />

        {/* Date Shimmer */}
        <Skeleton
          variant="text"
          width={180}
          height={20}
          sx={{ mb: 1.5 }}
        />

        {/* Description Shimmer */}
        <Skeleton
          variant="text"
          width="100%"
          height={18}
          sx={{ mb: 0.75 }}
        />
          <Skeleton
            variant="text"
          width="95%"
          height={18}
          sx={{ mb: 0.75 }}
          />
          <Skeleton
            variant="text"
          width="85%"
          height={18}
          sx={{ mb: 2 }}
          />

        {/* Tags Shimmer */}
        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            mb: 2,
          }}
        >
          <Skeleton
            variant="rounded"
            width={80}
            height={24}
            sx={{ borderRadius: 1.5 }}
          />
          <Skeleton
            variant="rounded"
            width={100}
            height={24}
            sx={{ borderRadius: 1.5 }}
          />
        </Box>

        {/* Prize Pool Shimmer */}
        <Box
          sx={{
            mt: "auto",
            pt: 2,
            borderTop: (theme) =>
              `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.08)"
              }`,
          }}
        >
          <Skeleton
            variant="text"
            width={80}
            height={16}
            sx={{ mb: 1 }}
          />
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Skeleton
            variant="rounded"
              width={80}
              height={32}
              sx={{ borderRadius: 2 }}
            />
            <Skeleton
              variant="rounded"
              width={80}
              height={32}
              sx={{ borderRadius: 2 }}
          />
          <Skeleton
            variant="rounded"
              width={80}
              height={32}
              sx={{ borderRadius: 2 }}
          />
          </Box>
        </Box>
      </Box>
    </Paper>
  );

  // Section Shimmer - matches the renderSection structure with glass-card
  const SectionShimmer = ({ title }: { title: string }) => (
    <Paper
      elevation={0}
      sx={{
        mb: 8,
        p: { xs: 3, md: 4 },
        borderRadius: 3,
        backgroundColor: "transparent",
        backdropFilter: "blur(12px)",
        border: (theme) =>
          `1px solid ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)"
          }`,
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 2px 8px rgba(0,0,0,0.1)"
            : "0 2px 8px rgba(0,0,0,0.05)",
      }}
    >
      <Box sx={{ mb: 4 }}>
        <HeadingGradient
          variant="h4"
          sx={{
            fontWeight: 700,
          }}
        >
          {title}
        </HeadingGradient>
      </Box>

      <Grid container spacing={3}>
        {[...Array(3)].map((_, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <QuestCardShimmer />
          </Grid>
        ))}
      </Grid>
    </Paper>
  );

  return (
    <Box sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        {/* Hero Section Shimmer */}
        <Paper
          elevation={0}
          sx={{
            mb: 8,
            p: { xs: 4, md: 6 },
            textAlign: "center",
            borderRadius: 3,
            backgroundColor: "transparent",
            backdropFilter: "blur(12px)",
            border: (theme) =>
              `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.08)"
              }`,
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 2px 8px rgba(0,0,0,0.1)"
                : "0 2px 8px rgba(0,0,0,0.05)",
          }}
        >
          <Skeleton
            variant="text"
            width={300}
            height={56}
            sx={{ mx: "auto", mb: 2 }}
          />
          <Skeleton variant="text" width="80%" height={28} sx={{ mx: "auto", mb: 1 }} />
          <Skeleton variant="text" width="70%" height={28} sx={{ mx: "auto", mb: 4 }} />
          
          {/* Stats Chips Shimmer */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: 1.5, md: 2 },
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {[...Array(3)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                width={140}
                height={36}
                sx={{
                  borderRadius: 2,
                }}
              />
            ))}
        </Box>
        </Paper>

        {/* Sections */}
        <SectionShimmer title="Current Challenges" />
        <SectionShimmer title="Upcoming Challenges" />
        <SectionShimmer title="Completed Challenges" />
      </Container>
    </Box>
  );
};

export default InkMastersPageShimmer;

